import { Component, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ModalHandler } from "../../../core/modal-handler";
import { ContractInviteResponse } from "../../../models/user";
import { LogService } from "../../../services/log.service";
import { PortalService } from "../../../services/portal-service.service";
import { ShellService } from "../../../services/shell.service";

@Component({
  standalone: false,
  selector: "app-invite-users-tab",
  templateUrl: "./invite-users-tab.component.html",
  styleUrls: ["./invite-users-tab.component.scss"],
})
export class InviteUsersTabComponent implements OnInit {
  InviteUsersForm: UntypedFormGroup;
  success = false;
  error: any = null;
  inviting = false;
  sentCount = 0;
  invites: ContractInviteResponse[] = [];
  loadingInvites = false;
  inviteActionToken: string = null;

  constructor(
    private fb: UntypedFormBuilder,
    private portalService: PortalService,
    private modalHandler: ModalHandler,
    private logService: LogService,
    public shellService: ShellService,
  ) {}

  ngOnInit() {
    this.InviteUsersForm = this.fb.group({
      contractNo: [this.shellService.selectedContractId, [Validators.required]],
      emails: this.fb.array([this.createEmailControl()]),
    });
    this.InviteUsersForm.controls.contractNo.valueChanges.subscribe(() => this.loadInvites());
    this.loadInvites();
  }

  get emails(): UntypedFormArray {
    return this.InviteUsersForm.get("emails") as UntypedFormArray;
  }

  addEmail(): void {
    this.emails.push(this.createEmailControl());
  }

  removeEmail(index: number): void {
    if (this.emails.length === 1) {
      this.emails.at(0).setValue("");
      return;
    }
    this.emails.removeAt(index);
  }

  async inviteUsers() {
    this.resetMessage();
    if (this.inviting || this.InviteUsersForm.invalid) return false;
    this.inviting = true;
    const contractNo = this.InviteUsersForm.controls.contractNo.value;
    const emails = this.emails.controls.map((control) => String(control.value || "").trim()).filter((email) => email.length > 0);
    try {
      for (const email of emails) {
        await this.portalService.createContractInvite(contractNo, { email });
      }
      this.sentCount = emails.length;
      this.success = true;
      this.InviteUsersForm.setControl("emails", this.fb.array([this.createEmailControl()]));
      await this.loadInvites();
    } catch (error) {
      this.error = error;
      this.logService.log("Invite users failed with: ", error);
    } finally {
      this.inviting = false;
    }
  }

  async loadInvites() {
    if (!this.InviteUsersForm || this.InviteUsersForm.controls.contractNo.invalid) return;
    this.loadingInvites = true;
    const contractNo = this.InviteUsersForm.controls.contractNo.value;
    try {
      this.invites = await this.portalService.getContractInvites(contractNo);
    } catch (error) {
      this.error = error;
      this.logService.log("Load invitations failed with: ", error);
    } finally {
      this.loadingInvites = false;
    }
  }

  async deleteInvite(invite: ContractInviteResponse) {
    if (!invite.token) return;
    const confirmed = await this.modalHandler.confirm(`Delete invitation for ${invite.email}?`, "Delete Invitation");
    if (!confirmed) return;
    this.resetMessage();
    this.inviteActionToken = invite.token;
    try {
      await this.portalService.deleteContractInvite(invite.contractNo, invite.token);
      await this.loadInvites();
    } catch (error) {
      this.error = error;
      this.logService.log("Delete invitation failed with: ", error);
    } finally {
      this.inviteActionToken = null;
    }
  }

  async resendInvite(invite: ContractInviteResponse) {
    if (!invite.token || !invite.expired) return;
    this.resetMessage();
    this.inviteActionToken = invite.token;
    try {
      await this.portalService.resendContractInvite(invite.contractNo, invite.token);
      this.sentCount = 1;
      this.success = true;
      await this.loadInvites();
    } catch (error) {
      this.error = error;
      this.logService.log("Resend invitation failed with: ", error);
    } finally {
      this.inviteActionToken = null;
    }
  }

  resetMessage() {
    this.success = false;
    this.error = null;
    this.sentCount = 0;
  }

  inviteDisabled(): boolean {
    return this.inviting || this.InviteUsersForm.invalid;
  }

  inviteActionDisabled(invite: ContractInviteResponse): boolean {
    return this.inviteActionToken === invite.token;
  }

  private createEmailControl() {
    return this.fb.control("", [Validators.required, Validators.email]);
  }
}
